import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { endpoints } from '../../config/api';
import { HeaderMain, FooterMain } from '../homepage';
import { FilterSection, TherapistCard } from '../common';
import { SEOHead } from '../seo';

function CityTherapistListings() {
  const { state, city } = useParams();
  const navigate = useNavigate();
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');
  const [sortBy, setSortBy] = useState('ranking');
  const [, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Advanced filter states
  const [filters, setFilters] = useState({
    insurance: '',
    sessionType: '',
    radius: '50',
    gender: '',
    professionalType: ''
  });

  // Format city and state names for display
  const cityName = city ? city.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  ).join(' ') : '';
  
  const stateAbbr = state ? state.toUpperCase() : '';
  
  const sortOptions = [
    { value: 'ranking', label: 'Best Match (Recommended)' },
    { value: 'name', label: 'Name A-Z' },
    { value: 'experience', label: 'Most Experience' },
    { value: 'rate-low', label: 'Price Low to High' },
    { value: 'rate-high', label: 'Price High to Low' }
  ];

  // Dynamic SEO configuration
  const seoConfig = {
    title: `Therapists in ${cityName}, ${stateAbbr}`,
    metaTitle: `Therapists in ${cityName}, ${stateAbbr}`,
    metaDescription: `Find licensed therapists in ${cityName}, ${stateAbbr}. Browse verified mental health professionals, compare rates, and book sessions with top-rated therapists in your area.`,
    keywords: `therapists ${cityName}, mental health ${stateAbbr}, counseling ${cityName}, therapy ${cityName} ${stateAbbr}, psychologists ${cityName}`,
    canonicalUrl: `https://www.therapylist.com/therapists/${state}/${city}`,
    ogTitle: `Find Therapists in ${cityName}, ${stateAbbr}`,
    ogDescription: `Discover qualified mental health professionals in ${cityName}, ${stateAbbr}. Licensed therapists, verified profiles, and easy online booking.`,
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": `Therapy Services in ${cityName}, ${stateAbbr}`,
      "description": `Licensed mental health therapy services in ${cityName}, ${stateAbbr}`,
      "provider": {
        "@type": "Organization",
        "name": "TherapyList"
      },
      "areaServed": {
        "@type": "City",
        "name": cityName,
        "addressRegion": stateAbbr
      }
    }
  };

  // Fetch therapists from API
  const fetchTherapists = async (page = 1, specialty = selectedSpecialty, sort = sortBy) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        location: `${cityName}, ${stateAbbr}`,
        page: page.toString(),
        limit: '10',
        sort: sort,
        radius: filters.radius
      });

      // Add specialty filter if not "All"
      if (specialty !== 'All') {
        params.append('specializations', specialty);
      }

      // Add filter parameters
      if (filters.insurance && filters.insurance !== '') {
        params.append('insurance', filters.insurance);
      }
      if (filters.sessionType && filters.sessionType !== '') {
        params.append('sessionType', filters.sessionType);
      }
      if (filters.gender && filters.gender !== 'All' && filters.gender !== '') {
        params.append('gender', filters.gender);
      }
      if (filters.professionalType && filters.professionalType !== '') {
        params.append('professionalType', filters.professionalType);
      }

      const response = await fetch(`${endpoints.search.therapists}?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch therapists: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setTherapists(data.therapists || []);
        setTotalResults(data.pagination?.totalCount || 0);
        setTotalPages(data.pagination?.totalPages || 0);
        
        // Track search impressions for all therapists shown
        if (data.therapists && data.therapists.length > 0) {
          const therapistIds = data.therapists.map(t => t._id);
          fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/analytics/search-impression`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ therapistIds }),
          }).catch(error => {
            console.warn('Failed to track search impressions:', error);
          });
        }
        
        // Check if there's a location-not-found message
        if (data.message && data.therapists.length === 0) {
          setError(data.message);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch therapists');
      }
    } catch (err) {
      console.error('Error fetching therapists:', err);
      setError(err.message);
      setTherapists([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (cityName && stateAbbr) {
      fetchTherapists(1, selectedSpecialty, sortBy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityName, stateAbbr, filters]);

  // Handle specialty filter change
  const handleSpecialtyChange = (specialty) => {
    setSelectedSpecialty(specialty);
    setCurrentPage(1);
    fetchTherapists(1, specialty, sortBy);
  };

  // Handle sort change
  const handleSortChange = (sort) => {
    setSortBy(sort);
    fetchTherapists(currentPage, selectedSpecialty, sort);
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTherapists(page, selectedSpecialty, sortBy);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
    setCurrentPage(1);
  };

  // Helper function to get session type label
  const getSessionTypeLabel = (value) => {
    const sessionTypes = {
      '': 'All',
      'in-person': 'In-Person',
      'telehealth': 'Online',
      'both': 'Both'
    };
    return sessionTypes[value] || value;
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      insurance: '',
      sessionType: '',
      radius: '50',
      gender: '',
      professionalType: ''
    });
    setSelectedSpecialty('All');
  };

  // Error state
  if (error) {
    const isLocationError = error.includes("couldn't find") || error.includes("check your spelling");
    
    return (
      <>
        <SEOHead {...seoConfig} />
        <HeaderMain />
        <div className="min-h-screen bg-[#e4fde1] flex items-center justify-center" style={{ fontFamily: 'Nunito Sans, sans-serif', marginTop: '-200px', paddingTop: '200px' }}>
          <div className="text-center max-w-2xl mx-auto px-4">
            <h1 className="text-2xl font-medium text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>
              {isLocationError ? 'Location Not Found' : 'Unable to Load Therapists'}
            </h1>
            <p className="text-[#254e70]/70 mb-6 text-lg">{error}</p>
            
            {isLocationError ? (
              <div className="space-y-4">
                <p className="text-[#254e70]/60">Try searching for one of these nearby cities:</p>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {['Louisville, KY', 'Nashville, TN', 'Atlanta, GA', 'Miami, FL', 'Chicago, IL'].map((city) => (
                    <button
                      key={city}
                      onClick={() => window.location.href = `/therapists/${city.split(', ')[1].toLowerCase()}/${city.split(', ')[0].toLowerCase().replace(/\s+/g, '-')}`}
                      className="bg-[#e4fde1] text-[#254e70] px-4 py-2 rounded-xl hover:bg-[#9dacff]/20 transition-colors text-sm font-medium"
                    >
                      {city}
                    </button>
                  ))}
                </div>
                <button 
                  onClick={() => window.location.href = '/find-therapists'}
                  className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors"
                >
                  Browse All Therapists
                </button>
              </div>
            ) : (
              <button 
                onClick={() => fetchTherapists(currentPage, selectedSpecialty, sortBy)}
                className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
        <FooterMain />
      </>
    );
  }

  return (
    <>
      <SEOHead {...seoConfig} />
      <HeaderMain />
      <div className="min-h-screen bg-[#e4fde1]" style={{ fontFamily: 'Nunito Sans, sans-serif', marginTop: '-200px', paddingTop: '200px' }}>
        {/* Header Section */}
        <div className="bg-[#e4fde1] border-b border-[#254e70]/10">
          <div className="max-w-7xl mx-auto px-4 py-8 lg:py-16">
            <div className="text-center space-y-4 lg:space-y-6">
              <h1 className="text-2xl lg:text-4xl xl:text-5xl font-medium text-[#254e70] leading-tight" style={{ fontFamily: 'Kanit, sans-serif' }}>
                Therapists in {cityName}, {stateAbbr}
              </h1>
              <p className="text-base lg:text-lg text-[#254e70]/80 max-w-3xl mx-auto">
                Find qualified mental health professionals in your area. All therapists are licensed and verified for your peace of mind.
              </p>
              <div className="flex flex-wrap justify-center gap-2 lg:gap-4 text-xs lg:text-sm">
                <div className="hidden sm:block bg-white/80 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full border border-[#254e70]/10">
                  <span className="text-[#254e70]/60 font-medium">Verified Profiles</span>
                </div>
                <div className="hidden md:block bg-white/80 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full border border-[#254e70]/10">
                  <span className="text-[#254e70]/60 font-medium">Local Providers</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto p-4 lg:p-8">
            <div className="lg:grid lg:grid-cols-4 lg:gap-8">
              {/* Filter Section - responsive mobile/desktop */}
              <div className="lg:col-span-1">
                <FilterSection
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  showLocationFilters={false}
                  selectedSpecialty={selectedSpecialty}
                  onSpecialtyChange={handleSpecialtyChange}
                  loading={loading}
                />

                {/* Sort Options - Keep separate on CityPage */}
                <div className="bg-white rounded-2xl border border-[#254e70]/10 p-4 mt-6">
                  <label className="block text-sm font-medium text-[#254e70] mb-2">Sort By</label>
                  <select
                    value={sortBy}
                    onChange={(e) => handleSortChange(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2 border border-[#254e70]/20 rounded-lg text-[#254e70] text-sm focus:border-[#254e70] focus:ring-1 focus:ring-[#254e70] outline-none disabled:opacity-50"
                  >
                    {sortOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Main Content */}
              <div className="lg:col-span-3">

          {/* Results Summary */}
          <div className="mb-8">
            <p className="text-[#254e70]/70" style={{ fontFamily: 'Nunito Sans, sans-serif' }}>
              {loading ? 'Loading...' : (
                <>
                  Showing therapists in {cityName}, {stateAbbr}
                  {selectedSpecialty !== 'All' && ` specializing in ${selectedSpecialty}`}
                  {filters.professionalType && filters.professionalType !== '' && ` • ${filters.professionalType}s`}
                  {filters.gender && filters.gender !== '' && ` • ${filters.gender} therapists`}
                  {filters.insurance && filters.insurance !== '' && ` • Accepts ${filters.insurance}`}
                  {filters.sessionType && filters.sessionType !== '' && ` • ${getSessionTypeLabel(filters.sessionType)} sessions`}
                </>
              )}
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#254e70]"></div>
            </div>
          )}

          {/* No Results */}
          {!loading && therapists.length === 0 && (
            <div className="text-center py-12">
              <h3 className="text-xl font-medium text-[#254e70] mb-4" style={{ fontFamily: 'Kanit, sans-serif' }}>
                No therapists found
              </h3>
              <p className="text-[#254e70]/70 mb-6">
                We couldn't find any therapists matching your criteria in {cityName}, {stateAbbr}.
              </p>
              <button 
                onClick={() => navigate('/find-therapists')}
                className="bg-[#254e70] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#1d3e5a] transition-colors"
              >
                Browse All Therapists
              </button>
            </div>
          )}

          {/* Therapist Listings */}
          {!loading && therapists.length > 0 && (
            <div className="space-y-6">
              {therapists.map((therapist) => (
                <TherapistCard
                  key={therapist._id}
                  therapist={therapist}
                  onViewProfile={(therapist) => {
                    if (therapist.slug) {
                      navigate(`/therapist/${therapist.slug}`);
                    } else {
                      console.log('No slug available for therapist:', therapist);
                    }
                  }}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-2 mt-12">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 rounded-xl border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Nunito Sans, sans-serif' }}
              >
                Previous
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, index) => {
                const page = Math.max(1, currentPage - 2) + index;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-4 py-2 rounded-xl font-medium transition-colors ${
                      currentPage === page
                        ? 'bg-[#254e70] text-white'
                        : 'border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1]'
                    }`}
                    style={{ fontFamily: 'Nunito Sans, sans-serif' }}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="px-4 py-2 rounded-xl border border-[#254e70]/20 text-[#254e70] hover:bg-[#e4fde1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                style={{ fontFamily: 'Nunito Sans, sans-serif' }}
              >
                Next
              </button>
            </div>
          )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <FooterMain />
    </>
  );
}

export default CityTherapistListings;